import React, { useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Button,
  Field,
  Input,
  Spinner,
  MessageBar,
  MessageBarBody,
  Card,
  Text,
} from '@fluentui/react-components';
import styles from './UrlShortener.module.scss';
import { IUrlShortenerProps } from './IUrlShortenerProps';
import { getAuthToken } from '../../auth/getAuthToken';
import { loginRequest } from '../../auth/msalConfig';
import strings from './strings';

interface ShortenResult {
  shortCode: string;
  shortUrl: string;
  longUrl: string;
}

interface LookupResult {
  shortCode: string;
  longUrl: string;
}

type Result = { type: 'shorten'; data: ShortenResult } | { type: 'lookup'; data: LookupResult };

function isShortUrl(value: string, shortDomain: string): boolean {
  try {
    const url = new URL(value);
    return url.hostname === new URL(shortDomain).hostname;
  } catch {
    return false;
  }
}

function extractCode(value: string): string {
  return value.split('/').filter(Boolean).pop() ?? value;
}

const UrlShortener: React.FC<IUrlShortenerProps> = ({ user, shortDomain }) => {
  const { instance, accounts } = useMsal();
  const [url, setUrl] = useState('');
  const [customSlug, setCustomSlug] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const looksLikeUrl = url.startsWith('http://') || url.startsWith('https://');
  const isLookup = isShortUrl(url, shortDomain) || (!looksLikeUrl && url.trim().length > 0);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);

    try {
      if (isLookup) {
        const code = extractCode(url);
        const res = await fetch(`/api/lookup/${encodeURIComponent(code)}`);
        if (res.status === 404) { setError(strings.errorNotFound); return; }
        if (!res.ok) { setError(strings.errorGeneral); return; }
        const data: LookupResult = await res.json();
        setResult({ type: 'lookup', data });
      } else {
        if (!user || !accounts[0]) {
          await instance.loginRedirect(loginRequest);
          return;
        }
        const token = await getAuthToken(accounts[0]);
        if (!token) { setError(strings.errorGeneral); return; }

        const body: { url: string; customSlug?: string } = { url };
        if (customSlug.trim()) body.customSlug = customSlug.trim();

        const res = await fetch('/api/shorten', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (res.status === 403) {
          const text = await res.text();
          setError(text.includes('approved') ? strings.errorDomainNotApproved : strings.errorGeneral);
          return;
        }
        if (res.status === 409) { setError(strings.errorConflict); return; }
        if (!res.ok) { setError(strings.errorGeneral); return; }
        const data: ShortenResult = await res.json();
        setResult({ type: 'shorten', data });
      }
    } catch {
      setError(strings.errorGeneral);
    } finally {
      setLoading(false);
    }
  }, [url, customSlug, isLookup, user, accounts, instance]);

  const handleCopy = useCallback(() => {
    if (result?.type === 'shorten') {
      navigator.clipboard.writeText(result.data.shortUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }, [result]);

  const showCustomSlug = !isLookup && !!user;

  return (
    <form className={styles.container} onSubmit={handleSubmit} aria-label={strings.title}>
      <Field label={strings.inputLabel} required className={styles.field}>
        <Input
          id="url-input"
          type="text"
          value={url}
          onChange={(_, d) => { setUrl(d.value); setResult(null); setError(null); }}
          placeholder={strings.inputPlaceholder}
          required
          aria-required="true"
          appearance="outline"
          size="medium"
        />
      </Field>

      {showCustomSlug && (
        <Field label={strings.customSlugLabel} className={styles.field}>
          <Input
            id="slug-input"
            value={customSlug}
            onChange={(_, d) => setCustomSlug(d.value)}
            placeholder={strings.customSlugPlaceholder}
            appearance="outline"
            size="medium"
          />
        </Field>
      )}

      <div className={styles.actions}>
        <Button
          appearance="primary"
          type="submit"
          disabled={loading || !url.trim()}
          icon={loading ? <Spinner size="tiny" /> : undefined}
        >
          {isLookup ? strings.submitLookup : strings.submitShorten}
        </Button>

        {!user && !isLookup && (
          <Button
            appearance="subtle"
            type="button"
            onClick={() => instance.loginRedirect(loginRequest)}
          >
            {strings.signIn}
          </Button>
        )}
      </div>

      {error && (
        <MessageBar intent="error" aria-live="assertive" className={styles.messageBar}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {result && (
        <Card className={styles.resultCard}>
          {result.type === 'shorten' && (
            <>
              <div className={styles.resultRow}>
                <Text weight="semibold" size={200} className={styles.resultLabel}>{strings.resultShortUrl}</Text>
                <Text className={styles.resultValue}>{result.data.shortUrl}</Text>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={handleCopy}
                  aria-label={strings.copyButton}
                >
                  {copied ? strings.copiedButton : strings.copyButton}
                </Button>
              </div>
              <div className={styles.resultRow}>
                <Text weight="semibold" size={200} className={styles.resultLabel}>{strings.resultLongUrl}</Text>
                <Text className={styles.resultValue}>{result.data.longUrl}</Text>
              </div>
            </>
          )}
          {result.type === 'lookup' && (
            <div className={styles.resultRow}>
              <Text weight="semibold" size={200} className={styles.resultLabel}>{strings.resultLongUrl}</Text>
              <Text className={styles.resultValue}>{result.data.longUrl}</Text>
            </div>
          )}
        </Card>
      )}
    </form>
  );
};

export default UrlShortener;
